import { useMemo, useState } from 'react';
import {
  App,
  Button,
  Descriptions,
  Empty,
  Modal,
  Popconfirm,
  Select,
  Skeleton,
  Space,
  Spin,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { ChevronLeft, UserPlus } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  iamApi as iamHttpApi,
  type GetPolicyResponse,
  type IamUserDetailGroupRecord,
  type ListIAMUserGroupsRequest,
} from '@/api/iam.api';
import { cn } from '@/lib/utils';
import { useIamUserDetail } from '@/pages/iam/composables/useIamUsers';
import {
  useAddUserToGroup,
  useBatchAddUserToGroup,
  useIamGroup,
  useIamUserGroupList,
} from '@/pages/iam/composables/useIamGroups';
import { useUserPolicyGrants } from '@/pages/iam/composables/useGrantPolicyData';
import { fmtTime } from '@/utils/utils';
import GroupPreviewDrawer from './components/GroupPreviewDrawer';
import PrincipalGrants from './components/PrincipalGrants';
import { GrantPermissionDrawer, PolicyPreviewDrawer } from '@/pages/iam/components';
import type { PolicyPreviewData } from '@/pages/iam/components/PolicySelector';
import './users.css';
import './user-detail.css';
import { LoadingOutlined } from '@ant-design/icons';

type UserDetailTab = 'groups' | 'permissions';

const ALL_GROUPS_REQUEST: ListIAMUserGroupsRequest = {
  page: 1,
  limit: -1,
  name: '',
  display_name: '',
  keyword: '',
};

function toPolicyPreview(policy: GetPolicyResponse['data']): PolicyPreviewData {
  return {
    id: policy.policy_id,
    name: policy.name,
    description: policy.description,
    type: policy.type === 'system' ? 'SYSTEM' : 'CUSTOM',
    authorizationCount: policy.authorization_count,
    createdAt: new Date(policy.created_at * 1000).toISOString(),
    statements: policy.policy_config.statement.map((statement) => ({
      effect: statement.effect.toLowerCase() === 'deny' ? 'Deny' : 'Allow',
      actions: statement.action,
      resourceScope:
        statement.condition?.string_equals?.['inf:resourceOwner'] === '{self}' ? 'OWN' : 'ALL',
    })),
    permissionActions: policy.permission_summary.actions,
    source: policy.policy_config,
  };
}

export default function UserDetail() {
  const { message } = App.useApp();
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [activeTab, setActiveTab] = useState<UserDetailTab>('groups');
  const [addToGroupsOpen, setAddToGroupsOpen] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [addingGroups, setAddingGroups] = useState(false);
  const [grantDrawerOpen, setGrantDrawerOpen] = useState(false);
  const [previewPolicyId, setPreviewPolicyId] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewGroup, setPreviewGroup] = useState<IamUserDetailGroupRecord>();
  const [previewGroupOpen, setPreviewGroupOpen] = useState(false);

  const { data: user, isLoading, error: userError } = useIamUserDetail(id);
  const userGrantsQuery = useUserPolicyGrants(id);
  const { data: groupsData, isLoading: areGroupsLoading } = useIamUserGroupList(
    ALL_GROUPS_REQUEST,
    addToGroupsOpen
  );
  const groupMembership = useAddUserToGroup();
  const batchGroupMembership = useBatchAddUserToGroup();
  const groupPreviewQuery = useIamGroup(previewGroup?.id);
  const groups = useMemo(() => groupsData?.items ?? [], [groupsData]);
  const previewGroupPolicies = useMemo(
    () =>
      (userGrantsQuery.data?.data.group_inheritance.records ?? []).filter(
        (record) => record.identity === previewGroup?.id
      ),
    [previewGroup?.id, userGrantsQuery.data]
  );

  const policyDetailQuery = useQuery({
    queryKey: ['iam', 'policies', 'detail', previewPolicyId] as const,
    queryFn: async () => {
      const response = await iamHttpApi.getPolicy(previewPolicyId as string);
      if (response.code !== 0) {
        throw new Error(response.message || '获取权限策略详情失败');
      }
      return response;
    },
    enabled: previewOpen && !!previewPolicyId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    staleTime: 0,
  });
  const previewPolicy = useMemo(
    () => (policyDetailQuery.data ? toPolicyPreview(policyDetailQuery.data.data) : undefined),
    [policyDetailQuery.data]
  );

  if (isLoading) {
    return (
      <div className="pp-page iam-page mx-auto w-full max-w-[1200px]">
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="pp-page iam-page mx-auto w-full max-w-[1200px]">
        <Empty
          description={
            userError instanceof Error ? `加载用户失败：${userError.message}` : '用户不存在'
          }
        />
      </div>
    );
  }

  const userId = user.id;
  const userName = user.name;
  const userLoginName = user.name;
  const enabled = user.enabled;
  const memberGroups = user.edges.groups ?? [];
  const grantCount =
    (userGrantsQuery.data?.data.direct_authorization.grant_total ?? 0) +
    (userGrantsQuery.data?.data.group_inheritance.grant_total ?? 0);

  async function addToGroups() {
    if (!selectedGroupIds.length) return;
    setAddingGroups(true);
    try {
      const memberGroupIds = new Set(memberGroups.map((group) => group.id));
      const targetGroups = groups.filter(
        (group) => selectedGroupIds.includes(group.id) && !memberGroupIds.has(group.id)
      );
      await batchGroupMembership.mutateAsync({
        user_id: userId,
        add_group_ids: targetGroups.map((group) => group.id),
      });
      message.success(`已将 ${userName} 添加至 ${targetGroups.length} 个用户组`);
      setAddToGroupsOpen(false);
      setSelectedGroupIds([]);
      setActiveTab('groups');
      queryClient.invalidateQueries({ queryKey: ['iam', 'groups'] });
      queryClient.invalidateQueries({ queryKey: ['iam', 'users'] });
    } finally {
      setAddingGroups(false);
    }
  }

  async function removeFromGroup(group: IamUserDetailGroupRecord) {
    try {
      await groupMembership.mutateAsync({
        group_id: group.id,
        add_users: [],
        remove_users: [userLoginName],
      });
      message.success('用户已从用户组移除');
      queryClient.invalidateQueries({ queryKey: ['iam', 'groups'] });
      queryClient.invalidateQueries({ queryKey: ['iam', 'users'] });
    } catch {
      // 当前操作不在业务层重复展示错误。
    }
  }

  function openPolicyPreview(policyId: string) {
    setPreviewPolicyId(policyId);
    setPreviewOpen(true);
  }

  function openGroupPreview(groupId: string) {
    const group = memberGroups.find((item) => item.id === groupId);
    if (!group) {
      navigate(`/iam/groups/${groupId}`);
      return;
    }
    setPreviewGroup(group);
    setPreviewGroupOpen(true);
  }

  const groupsPanel = (
    <div className="iam-user-tab-panel">
      <div className="iam-user-tab-head">
        <Button icon={<UserPlus size={14} />} onClick={() => setAddToGroupsOpen(true)}>
          添加至用户组
        </Button>
      </div>
      {memberGroups.length ? (
        <div className="iam-user-detail-list">
          {memberGroups.map((group) => (
            <div className="iam-user-detail-record iam-user-group-record" key={group.id}>
              <div className="iam-user-detail-record-main">
                <Button
                  type="link"
                  className="iam-user-group-record-link"
                  onClick={() => openGroupPreview(group.id)}
                >
                  {group.display_name || group.name}
                </Button>
                <Typography.Text type="secondary" className="iam-user-group-record-description">
                  {group.comment || '暂无描述'}
                </Typography.Text>
              </div>
              <div className="iam-user-detail-record-audit">
                <Typography.Text type="secondary">加入时间</Typography.Text>
                <Typography.Text>{fmtTime(group.joined_at)}</Typography.Text>
              </div>
              <Popconfirm
                title="从用户组移除？"
                description="移除后，用户将不再继承该组下的所有权限策略。"
                okText="确认移除"
                onConfirm={() => removeFromGroup(group)}
              >
                <Tooltip title="移出用户组">
                  <Button
                    type="text"
                    danger
                    aria-label={`将 ${userName} 从 ${group.display_name || group.name} 移除`}
                  >
                    移除
                  </Button>
                </Tooltip>
              </Popconfirm>
            </div>
          ))}
        </div>
      ) : (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂未加入用户组" />
      )}
    </div>
  );

  const permissionsPanel = (
    <PrincipalGrants
      identity={user.id}
      response={userGrantsQuery.data}
      loading={userGrantsQuery.isFetching}
      error={userGrantsQuery.error}
      onOpenPolicyPreview={openPolicyPreview}
      onOpenGroupPreview={openGroupPreview}
      onAddGrant={() => setGrantDrawerOpen(true)}
    />
  );

  return (
    <div
      className={cn('pp-page', 'iam-page', 'iam-user-detail-page', 'mx-auto w-full max-w-[1200px]')}
    >
      <header className="iam-user-detail-head">
        <Button
          className="iam-user-detail-back"
          type="text"
          aria-label="返回用户列表"
          icon={<ChevronLeft size={16} />}
          onClick={() => navigate('/iam/users')}
        />
        <div className="iam-user-detail-title">
          <Space size={8} wrap>
            <h2>{userName}</h2>
            <Tag bordered={false} color={enabled ? 'success' : 'default'}>
              {enabled ? '启用' : '禁用'}
            </Tag>
          </Space>
          <Typography.Text type="secondary">用户 ID：{user.id}</Typography.Text>
        </div>
        {/* 编辑用户名、修改密码、启停用与删除用户待真实接口接入后恢复。 */}
      </header>

      <div className="iam-user-detail-content">
        <section className="iam-user-basic-panel">
          <Descriptions
            bordered
            size="small"
            column={{ xs: 1, sm: 2, lg: 3 }}
            items={[
              { key: 'username', label: '用户名', children: userName },
              { key: 'id', label: '用户 ID', children: user.id },
              // {
              //   key: 'source',
              //   label: '身份来源',
              //   children: '—',
              // },
              { key: 'createdAt', label: '创建时间', children: fmtTime(user.created_at) },
              {
                key: 'lastLoginAt',
                label: '最后登录时间',
                children: fmtTime(user.last_login_at),
              },
            ]}
          />
        </section>

        <section className="iam-user-detail-tabs">
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as UserDetailTab)}
            className="iam-user-primary-tabs"
            items={[
              {
                key: 'groups',
                label: `所属用户组（${memberGroups.length}）`,
                children: groupsPanel,
              },
              {
                key: 'permissions',
                label: (
                  <span>
                    权限策略（
                    {userGrantsQuery.isPending ? (
                      <Spin
                        indicator={<LoadingOutlined spin />}
                        size="small"
                        aria-label="权限策略数量加载中"
                      />
                    ) : (
                      grantCount
                    )}
                    ）
                  </span>
                ),
                children: permissionsPanel,
              },
            ]}
          />
        </section>
      </div>

      <Modal
        title={`添加至用户组 · ${userName}`}
        open={addToGroupsOpen}
        okText="确认添加"
        confirmLoading={addingGroups}
        okButtonProps={{ disabled: !selectedGroupIds.length }}
        onOk={addToGroups}
        onCancel={() => {
          setAddToGroupsOpen(false);
          setSelectedGroupIds([]);
        }}
      >
        <Typography.Paragraph type="secondary">
          可一次选择多个用户组；该用户已经加入的用户组不可重复选择。
        </Typography.Paragraph>
        <Select
          mode="multiple"
          showSearch
          loading={areGroupsLoading}
          optionFilterProp="label"
          style={{ width: '100%' }}
          placeholder="选择一个或多个用户组"
          value={selectedGroupIds}
          onChange={setSelectedGroupIds}
          options={groups.map((group) => ({
            value: group.id,
            label: `${group.display_name || group.name} · ${group.comment || '暂无描述'}`,
            disabled: memberGroups.some((memberGroup) => memberGroup.id === group.id),
          }))}
        />
      </Modal>

      <GrantPermissionDrawer
        open={grantDrawerOpen}
        preset={{ principalType: 'USER', principalId: user.id }}
        onClose={() => setGrantDrawerOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['iam', 'grants'] });
        }}
      />

      <PolicyPreviewDrawer
        open={previewOpen}
        policy={previewPolicy}
        loading={policyDetailQuery.isFetching}
        error={
          policyDetailQuery.error
            ? policyDetailQuery.error instanceof Error
              ? policyDetailQuery.error.message
              : String(policyDetailQuery.error)
            : undefined
        }
        onClose={() => setPreviewOpen(false)}
        afterClose={() => setPreviewPolicyId(undefined)}
        onViewDetail={(policy) => {
          setPreviewOpen(false);
          navigate(`/iam/policies/${policy.id}`);
        }}
      />

      <GroupPreviewDrawer
        open={previewGroupOpen}
        group={groupPreviewQuery.data}
        fallbackGroup={previewGroup}
        policies={previewGroupPolicies}
        loading={groupPreviewQuery.isFetching}
        error={groupPreviewQuery.error}
        onClose={() => setPreviewGroupOpen(false)}
        afterClose={() => setPreviewGroup(undefined)}
        onOpenDetail={(groupId) => {
          setPreviewGroupOpen(false);
          navigate(`/iam/groups/${groupId}`);
        }}
      />
    </div>
  );
}
